import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../shared/scripts/table-column-visibility";
import {
    GetSelectedDropdownValues,
    IFilter,
    InitializeFilterDropdown,
    InitializeFilterOptGroupDropdown,
    DropdownOptGroup
} from "../../shared/scripts/dropdown-functions";
import { FormatDate } from "../../shared/scripts/date-functions";
import { DoArraysOverlap } from "../../shared/scripts/array-functions";
import _ from 'lodash';
import { EditManuscriptsPage } from "../manuscript-tracker/scripts/edit-manuscripts.page";
import 'highcharts';

import { KpiCardVm, KpiRow } from "../../shared/scripts/kpi-row";
import { ChartCard } from "../../shared/scripts/chart-card";
import { PublicationsFiltersVm } from "../../general/network-impact/publications/publications.page";
import { buildCountTrend } from "../../shared/scripts/kpi-trend";
import { DonutChartData, DonutChart } from "../../shared/scripts/donut-chart";
import { RankChart } from "../../shared/scripts/rank-chart";

import {
    isInLast12Months,
    getSelectionText, renderDataTableSearchBox,
    setSingleDropdownOptionByText, clearDropdown, isInPrevious12Months,
} from '../../shared/scripts/filter-ui-helpers';

// --- VM from model ---

export interface PublicationsVm {
    TargetJournal: string;
    PubMedId: string;
    Doi: string;
    Pmcid: string;
}

// Server-precomputed filters
export interface CoreProjGroupVm {
    Center: string;
    Tokens: string[];
}
export interface FiltersVm {
    Statuses: string[];
    Centers: string[];
    CollaboratingCenters: string[];
    PrimaryAuthors: string[];
    SeniorAuthors: string[];
    NetworkAuthors: string[];
    Journals: string[];
    CoreProjectByCenter: CoreProjGroupVm[];
}
export interface PageVm {
   // Items: ManuscriptVm[];
    Filters: FiltersVm;
}
export interface ManuscriptTrackerPageVm {
    //items: ManuscriptVm[];
    filters: FiltersVm;

    publications: PubDtRow[];
    publicationsLastUpdatedUtc?: string; // ISO string
    publicationsFilters?: PublicationsFiltersVm;

}

export type PubDtRow = {
    CenterNames: string[];
    AwardNumbers: string[];
    FundingGrants: string[];

    CenterNameJoined: string;
    AwardNumberJoined: string;
    FundingGrantsJoined: string;

    PubMedId: string;
    Title: string;
    Authors: string;
    AuthorsList: string[];
    PubYear: number | null;

    Journal: string;
    JournalNorm: string;

    PublicationDateUtc: string | null;
    PublicationDateDisplay: string;

    Doi: string;
    Pmcid: string;
    Abstract: string;

    MeshTerms: string[];
    MeshTermsJoined: string;
    Keywords: string[];
    KeywordsJoined: string;

    IsPreprint: boolean;
    MatchKey: string;
    DaysFromPreprintToPublished?: number | null;
};

type Input = string | ManuscriptTrackerPageVm | PageVm | { Filters?: FiltersVm };


const EmptyDisplay = '-none-';

type MsInnerTab = 'overview' | 'authors';

const MISSING_AWARD_LABEL = 'Missing award (update on PubMed)';
const MISSING_CENTER_LABEL = '-- Unknown --';


export class ManuscriptTrackerPage {
    //Rows for Pub and filters
    private pubsFiltersInited = false;

 
    private chartCard?: ChartCard;
    private pubsRowsAll: PubDtRow[] = [];
  
    private filters?: FiltersVm;

    private state = {
        centers: null as string[] | null,
        authors: null as string[] | null,
        journals: null as string[] | null,
        years: null as string[] | null,
        preprints: null as string[] | null,
        range: 'all' as 'all' | 'last12'
    };
   

    private editMsInited = false;
    private editMsPage?: EditManuscriptsPage;


    private publications: PubDtRow[] = [];
    private publicationsFilters?: PublicationsFiltersVm;


    constructor(input: Input) {
        const { filters, publications, publicationsFilters } = this.parseInput(input);
    
        this.filters = filters;
        //1. full data set
        this.publications = publications ?? [];
        this.publicationsFilters = publicationsFilters;
        //update time on load
        const el = document.getElementById('pubs-last-updated');
        const utc = el?.getAttribute('data-utc');

        if (el && utc) {
            el.textContent = this.formatLocalRazorStyle(new Date(utc));
            el.classList.remove('d-none');
        }

    
        this.wireSimpleTooltips();    

        this.wireTopTabs();

        // init the active top tab module once on load
        requestAnimationFrame(() => {
            if ($('#pubs-tab').hasClass('active') || $('#pubs-pane').hasClass('active')) {
                this.ensurePublicationsModule();
            }
        });

    }


    // --- Parse input ---
    private parseInput(input: any): {
        filters?: FiltersVm;
        publications?: PubDtRow[];
        publicationsLastUpdatedUtc?: string;
        publicationsFilters?: PublicationsFiltersVm;
    } {
        const normalize = (obj: any) => ({
            filters: obj?.Filters ?? obj?.filters,
            publications: obj?.Publications ?? obj?.publications ?? [],
            publicationsLastUpdatedUtc: obj?.PublicationsLastUpdatedUtc ?? obj?.publicationsLastUpdatedUtc,
            publicationsFilters: obj?.PublicationsFilters ?? obj?.publicationsFilters
        });

        if (typeof input === "string") {
            try { return normalize(JSON.parse(input)); }
            catch (e) {
                console.error("Failed to parse ManuscriptsJson", e);
                return {};
            }
        }

        return normalize(input);
    }

    // --- Helpers reused by filters/CSV ---

    private isAllSelected(selectId: string, selected: string[] | null | undefined): boolean {
        if (!selected || !selected.length) return false;

        const options = document.querySelectorAll<HTMLOptionElement>(`${selectId} option`);
        const uniqueValues = new Set(Array.from(options, o => o.value));

        if (uniqueValues.size === 0) return false;

        const selectedSet = new Set(selected);
        for (const v of uniqueValues) {
            if (!selectedSet.has(v)) return false;
        }
        return true;
    }

    private readSel(selectId: string): string[] | null {
        const $el = $(selectId);
        if ($el.length === 0) return null;

        if ($el.find('option').length === 0) return null;

        const raw = GetSelectedDropdownValues(selectId) as string[] | null;
        const sel = raw ?? [];

        // none selected => match nothing
        if (sel.length === 0) return [];

        // all selected => treat as no filter
        if (this.isAllSelected(selectId, sel)) return null;

        return sel;
    }


    private matchesOne(sel: string[] | null, value?: string): boolean {
        if (sel == null) return true;     // no filter
        if (sel.length === 0) return false; // match nothing
        return value ? sel.includes(value) : false;
    }

    private matchesAny(sel: string[] | null, values?: string[]): boolean {
        if (sel == null) return true;
        if (sel.length === 0) return false;
        return DoArraysOverlap(sel, (values ?? []).filter(Boolean));
    }

    // ==============================
    // CSV Export
    // ==============================

    private buildCsv(headers: string[], rows: string[][]) {
        const esc = (s: any) => `"${String(s).replace(/"/g, '""')}"`;
        return '\uFEFF' + [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
    }

    private downloadCsv(csv: string, filename: string) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
    }

    // --- CSV for Publications
    private exportCsvForVisiblePublications(dt: DataTables.Api, baseName: string) {
        const rows = dt.rows({ search: 'applied', page: 'all' }).data().toArray() as PubDtRow[];

        const V = (x: unknown) => (x == null ? '' : String(x).trim());
        const L = (arr: any) =>
            (Array.isArray(arr) ? arr : [])
                .map((s: any) => V(s))
                .filter(Boolean)
                .join('; ');

        const H = [
            'Title',
            'Authors',
            'Journal',
            'Award Number',
            'Publication Date',
            'Centers',
            'Abstract',
            'MeSH Terms',
            'PMID',
            'PMCID',
            'DOI',
            'Preprint',
            'Funding / Grants',

        ];

        const data = rows.map(r => ([
            V(r.Title),
            V(r.Authors),
            V(r.Journal),
            V(r.AwardNumberJoined),

            V(r.PublicationDateDisplay || r.PublicationDateUtc),
            V(r.CenterNameJoined),
            V(r.Abstract),
            V(r.MeshTermsJoined) ,
            V(r.PubMedId),
            V(r.Pmcid),
            V(r.Doi),
            r.IsPreprint ? 'Yes' : 'No',
            V(r.FundingGrantsJoined),


        ]));

        const csv = this.buildCsv(H, data);
        const today = new Date().toISOString().slice(0, 10);
        this.downloadCsv(csv, `${baseName}_${today}.csv`);
    }

  

    // ==============================
    // Modal Helper- PILLS
    // ==============================

    private renderPills($host: JQuery, items: string[]) {
        const pillClass = ($host.attr('data-pill-class') || 'pill').trim();
        $host.empty();

        const clean = (items || []).filter(x => x && x.trim().length);
        if (!clean.length) {
            $host.text(EmptyDisplay);
            return;
        }

        clean.forEach(txt => {
            const $pill = $('<span/>', { class: pillClass }).append(
                $('<span/>', { text: txt })
            );
            $host.append($pill);
        });
    }


    // ==============================
    // Publications top tabs
    // ==============================

    // Nav Publications + publications Panes
    private wireTopTabs(): void {
        const sel = '#ms-top-tabs a[data-toggle="tab"]';
        $(sel).off('shown.bs.tab').on('shown.bs.tab', (e) => {
            const href = ($(e.target).attr('href') || '').toLowerCase();

            if (href === '#pubs-pane') {
                this.ensurePublicationsModule();
                // V2. Reflow chart after container is visible again
               // setTimeout(() => {
                   // try { (Highcharts as any).charts?.forEach((c: any) => c && c.reflow && c.reflow()); } catch { }
                //}, 0);

            }
            if (href === '#edit-ms-pane') this.ensureEditManuscriptsModule();

        });
    }


    // ==============================
    // Ensure Edit Manuscripts Partial
    // ==============================

    private ensureEditManuscriptsModule(): void {
        if (this.editMsInited) {
            try { (this.editMsPage?.manuscriptsTable as any)?.resizeTable?.(); } catch { }
            return;
        }

        this.editMsPage = new EditManuscriptsPage();
        this.editMsPage.initEditPane();


        (this as any).manuscriptsTable = this.editMsPage.manuscriptsTable;

        setTimeout(() => {
            try { (this.editMsPage?.manuscriptsTable as any)?.resizeTable?.(); } catch { }
        }, 0);

        this.editMsInited = true;
    }
 

    // ==============================
    // Publications Module
    // ==============================
  

    private pubsInited = false;
    private dtPubsStandalone?: DataTables.Api;
    private ensurePublicationsModule(): void {
        if (this.pubsInited) {
            try { this.dtPubsStandalone?.columns.adjust().draw(false); } catch { }
            this.updatePubsCount();
            return;
        }


        // -- Mapping helpers
        const rows: PubDtRow[] = this.publications;



        this.pubsRowsAll = rows;
        this.rebuildPubsIndex(this.pubsRowsAll);
        if (!this.pubsFiltersInited) {
            this.buildPublicationsFilters();
        }

        const dom = `
    <"d-flex justify-content-end mb-2 pubs-buttons"B>
    <"pubs-search"f>
    rtip
`;

        this.dtPubsStandalone = ($('#pubsTable') as any).DataTable({
            dom,
            paging: false,
            info: false,
            searching: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            fixedColumns: 1,
            //order by publication date
            order: [[4, "desc"], [0, "asc"]],

            deferRender: true,
            data: rows,


            buttons: [{
                text: `<i class="fas fa-file-download"></i>`,
                titleAttr: 'CSV',
                action: (_e: any, api: DataTables.Api) =>
                    this.exportCsvForVisiblePublications(api, 'Revampp-Published-Manuscripts')
            }],
            columns: [
                // Title + preprint badge
                {
                    data: 'Title',
                    className: 'b-fixed-left font-size12 text-left table-column-lg ',
                    title: 'Title',
                    defaultContent: '',
                    render: (d: string, type: string, row: PubDtRow) => {
                        const title = (d || '').trim();
                        if (type !== 'display') return title;

                        const key = this.getPubKey(row);

                        return `
      <a href="#" class="pub-title-link line-height font-weight-500"
         data-pubkey="${_.escape(key)}"
         title="View publication details">
        ${_.escape(title || 'Untitled')}
      </a>`;
                    }

                },
                // Center and Award - Render warning msg and tooltip when missing award number.
                {
                    data: 'CenterNameJoined',
                    className: 'text-left font-size12 table-column-sm',
                    title: 'Center',
                    defaultContent: '',
                    render: (d: string, type: string, row: PubDtRow) => {
                        const v = (d || '').trim();
                        if (type !== 'display') return v;

                        // Center missing because award is missing
                        if (!v || v === MISSING_CENTER_LABEL) {
                            const msg =
                                'This publication has no award number on PubMed, so it cannot be associated with a center. ' +
                                'Add or update funding information on the PubMed record (PMID).';

                            return `
        <span class="text-warning font-italic js-tip"
              data-tooltip="${_.escape(msg)}">
          ${_.escape(MISSING_CENTER_LABEL)}
          <i class=" fas fa-question-circle ml-1"></i>
        </span>`;
                        }

                        return _.escape(v);
                    }
                },
                {
                    data: 'Authors', className: 'text-left font-size12 table-column-lg', title: 'Authors', defaultContent: '',
                    render: (abs?: string, type?: string) => {
                        const v = (abs || '').trim();
                        if (!v) return '';
                        if (type && type !== 'display') return v;
                        const short = v.length > 120 ? v.slice(0, 120) + '…' : v;
                        return `<span title="${_.escape(v)}">${_.escape(short)}</span>`;
                    }
                },

                { data: 'Journal', className: 'text-left font-size12 table-column-md', title: 'Journal', defaultContent: '' },
                {
                    data: 'PublicationDateUtc', //  sort key
                    title: 'Pub Date',
                    className: 'text-left font-size12 table-column-sm',
                    defaultContent: '',
                    render: (_utc?: string, type?: string, row?: PubDtRow) => {
                        const utc = (row?.PublicationDateUtc || '').trim();
                        if (!utc) return '';
                        if (type && type !== 'display') return utc; //  sorting/searching uses UTC
                        return row?.PublicationDateDisplay || utc;
                    }
                },

                // Preprint Col 
                {
                    data: 'IsPreprint',
                    title: 'Preprint',
                    className: 'text-left font-size12 nowrap table-column-xs',
                    defaultContent: '',
                    render: (v?: boolean, type?: string) => {
                        if (type && type !== 'display') return v ? 1 : 0; // numeric sort
                        return v ? 'Yes' : 'No';
                    }
                },

                {
                    data: 'AwardNumberJoined', className: 'text-left font-size12 table-column-sm', title: 'Award Number', defaultContent: '', render: (d: string, type: string) => {
                        const v = (d || '').trim();
                        if (type !== 'display') return v;

                        if (v === MISSING_AWARD_LABEL) {
                            const msg = 'Award number missing in PubMed metadata. Update funding information on the PMID record.';
                            return `
                        <span class="text-warning font-italic js-tip"
                              data-tooltip="${_.escape(msg)}">
                          ${_.escape(v)}
                          <i class="fas fa-question-circle ml-1"></i>
                        </span>`;
                        }


                            return _.escape(v);
                     }
                 },

                {
                    data: 'Abstract',
                    title: 'Abstract',
                    className: 'text-left font-size12 table-column-lg',
                    defaultContent: '',
                    render: (abs?: string, type?: string) => {
                        const v = (abs || '').trim();
                        if (!v) return '';
                        if (type && type !== 'display') return v;
                        const short = v.length > 120 ? v.slice(0, 120) + '…' : v;
                        return `<span title="${_.escape(v)}">${_.escape(short)}</span>`;
                    }
                },

                // PMID link
                {
                    data: 'PubMedId',
                    title: 'PMID',
                    className: 'text-left font-size12 nowrap table-column-sm',
                    defaultContent: '',
                    render: (pmid?: string, type?: string) => {
                        const v = (pmid || '').trim();
                        if (!v) return '';
                        if (type && type !== 'display') return v;
                        return `<a href="https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(v)}/" class= "external" target="_blank" rel="noopener">${_.escape(v)}</a>`;
                    }
                },
                {
                    data: 'Pmcid',
                    title: 'PMCID',
                    className: 'text-left font-size12 nowrap table-column-sm',
                    defaultContent: '',
                    render: (pmcid?: string, type?: string) => {
                        const v = (pmcid || '').trim();
                        if (!v) return '';
                        if (type && type !== 'display') return v;

                        // PMCID links can go to PMC
                        return `<a href="https://pmc.ncbi.nlm.nih.gov/articles/${encodeURIComponent(v)}/"
              class="external" target="_blank" rel="noopener">${_.escape(v)}</a>`;
                    }
                },
                {
                    data: 'Doi',
                    title: 'DOI',
                    className: 'text-left font-size12 nowrap table-column-md',
                    defaultContent: '',
                    render: (doi?: string, type?: string) => {
                        const v = (doi || '').trim();
                        if (!v) return '';
                        if (type && type !== 'display') return v;
                        return `<a href="https://doi.org/${encodeURIComponent(v)}" target="_blank" class= "external" rel="noopener">${_.escape(v)}</a>`;
                    }
                },

                {
                    data: 'FundingGrantsJoined',
                    title: 'Funding / Grants',
                    className: 'text-left font-size12 table-column-lg',
                    defaultContent: '',
                },

                // Hidden Mesh and KeyWords ------
                { data: 'MeshTermsJoined', title: 'MeSH', className: 'text-left font-size12 table-column-lg', defaultContent: '', visible: false },

                {
                    data: 'KeywordsJoined',
                    title: 'KeywordsJoined',
                    className: 'text-left font-size12 table-column-lg',
                    defaultContent: '',
                    visible: false

                },
            ]

        }) as DataTables.Api;

        
        const $searchInput = renderDataTableSearchBox({
            sourceSelector: '.pubs-search .dataTables_filter',
            targetSelector: '#custom-search-container',
            placeholder: 'Search Publications'
        });

        $searchInput
            .off('input.pubsVisualSync')
            .on('input.pubsVisualSync', () => {
                this.schedulePublicationsVisualSync();
            });
        $('.pubs-buttons .dt-buttons').addClass('mb-0');
        this.wirePublicationsRowActions();

        this.registerChartCardVisualization();
        this.registerDonutVisualization();
        this.registerKpiVisualization();
        this.registerTopJournalRankVisualization();
        this.kpiRow?.wireEvents();
        this.chartCard?.init();
        this.donutChart?.init();
        this.rankChart?.init();
        //5. Trigger filter for selection
        this.triggerFilter();




        $('#pubs-reset').off('click').on('click', () => {
            this.dtPubsStandalone?.search('');

            this.state.range = 'all';

            // These reset to All
            ['#sel-pubs-center', '#sel-pubs-year', '#sel-pubs-preprint', '#sel-pubs-author', '#sel-pubs-journal']
                .forEach(sel => {
                    const $dd = $(sel);
                    $dd.find('option').prop('selected', false);
                    try { ($dd as any).selectpicker('refresh'); } catch { }
                });


            this.triggerFilter();
        });
        
        this.pubsInited = true;

        requestAnimationFrame(() => {
            try { this.dtPubsStandalone?.columns.adjust(); } catch { }
        });

        this.updatePubsCount();
        this.wirePublicationsRefresh();
    }


    //Generates a stable publication ID (best available key).
    private getPubKey(r: Partial<PubDtRow>): string {
        const mk = String(r.MatchKey ?? '').trim();
        if (mk) return mk;

        const pmid = String(r.PubMedId ?? '').trim();
        if (pmid) return `pmid:${pmid}`;


        const title = String(r.Title ?? '').trim().toLowerCase();
        const journal = String(r.Journal ?? '').trim().toLowerCase();
        const year = r.PubYear != null ? String(r.PubYear) : '';
        const doi = String((r as any).Doi ?? '').trim().toLowerCase();
        return `${doi}|${journal}|${year}|${title}`.replace(/\s+/g, ' ');
    }



    // ==============================
    // Pubs Count + Filters
    // ==============================

    private updatePubsCount(): void {
        const dt = this.dtPubsStandalone;
        const count = dt ? dt.rows({ search: 'applied' }).count() : 0;
        $('#pubs-count').text(`${count} Publication${count === 1 ? '' : 's'} Found`);
    }

    private pubsIndex = new Map<string, PubDtRow>();
    private rebuildPubsIndex(rows: PubDtRow[]) {
        this.pubsIndex.clear();
        rows.forEach(r => {
            const key = this.getPubKey(r);
            this.pubsIndex.set(key, r);
        });
    }

    private readSelNoSelectionMeansAll(selectId: string): string[] | null {
        const $el = $(selectId);
        if ($el.length === 0) return null;
        if ($el.find('option').length === 0) return null;

        const raw = GetSelectedDropdownValues(selectId) as string[] | null;
        const sel = (raw ?? [])
            .map(s => String(s ?? '').trim())
            .filter(Boolean);

        // none selected => treat as no filter
        if (sel.length === 0) return null;

        // all selected => treat as no filter
        if (this.isAllSelected(selectId, sel)) return null;

        return sel;
    }
    //7. clear table, add filtered rows, redraw, update count. SOT= table
    private updatePublicationsView(rows: PubDtRow[]): void {
        if (!this.dtPubsStandalone) return;

        this.dtPubsStandalone.clear();
        this.dtPubsStandalone.rows.add(rows).draw();

        this.updatePubsCount();
        this.renderKpis(rows);
        this.renderChartCard(rows);
        this.renderDonut(rows);
        this.renderRankChart(rows);
    }   
    private triggerFilter(): void {
      
        this.syncStateFromUi();

        const s = this.state;
        let rows = [...this.publications];

        rows = rows.filter(row => {
            const okCenter = this.matchesAny(s.centers, row.CenterNames);
            const okAuthor = this.matchesAny(s.authors, row.AuthorsList);

            const okJournal =
                s.journals == null
                    ? true
                    : s.journals.some(j => j.trim().toLowerCase() === row.JournalNorm);

            const okYear = this.matchesOne(
                s.years,
                row.PubYear != null ? String(row.PubYear) : ''
            );

            const okPreprint = this.matchesOne(
                s.preprints,
                row.IsPreprint ? 'Yes' : 'No'
            );

            return okCenter && okAuthor && okJournal && okYear && okPreprint;
        });
        if (s.range === 'last12') {
            rows = rows.filter(row => isInLast12Months(row.PublicationDateUtc));
        }

        this.updatePublicationsView(rows);
    }

    private syncStateFromUi(): void {
        this.state.centers = this.readSelNoSelectionMeansAll('#sel-pubs-center');
        this.state.authors = this.readSelNoSelectionMeansAll('#sel-pubs-author');
        this.state.journals = this.readSelNoSelectionMeansAll('#sel-pubs-journal');
        this.state.years = this.readSelNoSelectionMeansAll('#sel-pubs-year');
        this.state.preprints = this.readSelNoSelectionMeansAll('#sel-pubs-preprint');
        
    }
    // 2. Build Pub filters
    private buildPublicationsFilters(): void {
        $('#pubs-filter-container').html(`
        <div class="row no-gutters align-items-end">
            <div class="col px-1">
                <label class="mb-1 font-size12">Center</label>
                <select id="sel-pubs-center" class="selectpicker" multiple
                        data-actions-box="true" data-live-search="true"
                        data-width="100%" title="None Selected"></select>
            </div>

            <div class="col px-1">
                <label class="mb-1 font-size12">Author</label>
                <select id="sel-pubs-author" class="selectpicker" multiple
                        data-actions-box="true" data-live-search="true"
                        data-width="100%" title="None Selected"></select>
            </div>

            <div class="col px-1">
                <label class="mb-1 font-size12">Journal</label>
                <select id="sel-pubs-journal" class="selectpicker" multiple
                        data-actions-box="true" data-live-search="true"
                        data-width="100%" title="None Selected"></select>
            </div>

            <div class="col px-1">
                <label class="mb-1 font-size12">Year</label>
                <select id="sel-pubs-year" class="selectpicker" multiple
                        data-actions-box="true" data-live-search="true"
                        data-width="100%" title="None Selected"></select>
            </div>

            <div class="col px-1">
                <label class="mb-1 font-size12">Preprint</label>
                <select id="sel-pubs-preprint" class="selectpicker" multiple
                        data-actions-box="true"
                        data-width="100%" title="None Selected"></select>
            </div>
        </div>
    `);

        const f = this.publicationsFilters;
        //2.2. InitializeFilterDropDown dummy triggerfilter object
        const noopFilter = {
            triggerFilter: () => { }
        };

        InitializeFilterDropdown('#sel-pubs-center', (f?.Centers ?? []).slice(), noopFilter, undefined, false);
        InitializeFilterDropdown('#sel-pubs-author', (f?.Authors ?? []).slice(), noopFilter, undefined, false);
        InitializeFilterDropdown('#sel-pubs-journal', (f?.Journals ?? []).slice(), noopFilter, undefined, false);
        InitializeFilterDropdown('#sel-pubs-year', (f?.Years ?? []).map(String) ?? [], noopFilter, undefined, false);
        InitializeFilterDropdown('#sel-pubs-preprint', (f?.Preprints ?? ['Yes', 'No']).slice(), noopFilter, undefined, false);

        this.pubsFiltersInited = true;

        // 3. new wiring for page-level filtering
        $('#pubs-filter-container')
            .find('select')
            .off('changed.bs.select')
            .on('changed.bs.select', () => this.triggerFilter());

    }



    // ==============================
    // Publication Refresh 
    // ==============================

    private pubsRefreshInited = false;

    private wirePublicationsRefresh(): void {
        if (this.pubsRefreshInited) return;

        const btn = document.getElementById('pubs-refresh');
        const url =
            btn?.getAttribute('data-refresh-url') ||

            //Json refresh---------

            (window.location.pathname + '?handler=PublicationsJson');

        $('#pubs-refresh').off('click').on('click', async () => {
            try {
                // Hide filters+table, disable keys, show loading
                this.setPubsLoading(true);
               
                await new Promise(requestAnimationFrame);

                const res = await fetch(url, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const payload = await res.json();
                //update timestamp div
                const el = document.getElementById('pubs-last-updated');
                const lastUtc = payload?.publicationsLastUpdatedUtc ?? payload?.PublicationsLastUpdatedUtc;

                if (el && lastUtc) {
                    el.textContent = this.formatLocalRazorStyle(new Date(lastUtc));
                    el.classList.remove('d-none');
                }

                const rows = (payload?.Publications ?? payload?.publications ?? []) as PubDtRow[];
                this.publications = rows;
                this.pubsRowsAll = rows;
                this.rebuildPubsIndex(rows);


                // Rebuild dropdown options from new data
                this.pubsFiltersInited = false;
                this.buildPublicationsFilters();

               
                requestAnimationFrame(() => {
                    try { this.dtPubsStandalone?.columns.adjust(); } catch { }
                    try { ($('#pubs-filter-container .selectpicker') as any).selectpicker('refresh'); } catch { }
                });



            } catch (e) {
                console.error('[PUBS] AJAX refresh failed', e);
                alert('Refresh failed. Check console for details.');
            } finally {
                // Show filters + table again
                this.setPubsLoading(false);
            }
        });

        this.pubsRefreshInited = true;
    }

    private setPubsLoading(isLoading: boolean): void {
        $('#pubs-loading').toggleClass('d-none', !isLoading);
        $('#pubs-body').toggleClass('d-none', isLoading);

        // Hide count + disable buttons while loading
        $('#pubs-count').toggleClass('d-none', isLoading);
        $('#pubs-reset').prop('disabled', isLoading);
        $('#pubs-refresh').prop('disabled', isLoading);
        $('#pubs-refresh').prop('isloading', isLoading);
    }


    // ==============================
    // Pub Row trigger Modal
    // ==============================

    private pubsRowActionsInited = false;

    private wirePublicationsRowActions(): void {
        if (this.pubsRowActionsInited) return;


        $(document)
            .off('click', 'a.pub-title-link')
            .on('click', 'a.pub-title-link', (e) => {
                e.preventDefault();

                const key = String((e.currentTarget as HTMLElement).getAttribute('data-pubkey') || '').trim();
                const vm = this.pubsIndex.get(key);

                const $modal = $('#pub-detail-modal');
                if (!vm) {
                    $modal.find('.modal-body').prepend('<div class="alert alert-danger">Publication not found.</div>');
                    $modal.modal('show');
                    return;
                }
                $modal.find('.alert').remove();

                this.fillPubModal(vm);
                $modal.modal('show');

            });

        this.pubsRowActionsInited = true;
    }

    private fillPubModal(p: PubDtRow): void {
        const $modal = $('#pub-detail-modal');

        const setText = (sel: string, val?: string | null) => {
            const $el = $modal.find(sel);
            const v = (val ?? '').trim();
            $el.text(v || EmptyDisplay);
        };

        const setPills = (sel: string, items?: string[] | string | null) => {
            const $el = $modal.find(sel);
            const arr =
                Array.isArray(items) ? items :
                    (typeof items === 'string' ? items.split(/;|,/g) : []);
            this.renderPills($el, arr.map(x => String(x ?? '').trim()).filter(Boolean));
        };

        const pillsCount = (sel: string): number => $modal.find(sel).find('span').length;

        // -----------------------------
        // Header
        // -----------------------------
        setText('[data-pub="Title"]', p.Title);

        // "Last Updated:" / date line
        setText('[data-pub="PubDate"]', p.PublicationDateDisplay || p.PublicationDateUtc || '');

        // -----------------------------
        // Block 1: Center, Publication Status, Journal
        // -----------------------------
        const MISSING_CENTER_MSG =
            'No center was detected for this publication. Missing ReVAMPP Award Number in PubMed.';

        const centers = (p.CenterNames ?? []).map(x => String(x ?? '').trim()).filter(Boolean);
        const $centersHost = $modal.find('[data-pub="Centers"]');

        // Clear whatever was there
        $centersHost.empty().removeClass('d-none');

        if (centers.length) {
            // Normal case: show pills
            this.renderPills($centersHost, centers);
        } else {
            // Empty center: show warning text (NOT a pill)
            $centersHost.html(`
    <div class="text-warning font-italic">
      ${_.escape(MISSING_CENTER_MSG)}
    </div>
  `);
        }
        // Always show a status pill: Preprint OR Published
        const status = p.IsPreprint ? 'Preprint' : 'Published'; setPills('[data-pub="PublicationStatus"]',
            [status]); $modal.find('[data-pub="PublicationStatus"]').toggleClass('d-none', pillsCount('[data-pub="PublicationStatus"]') === 0);
        // Journal 
        const journal = (p.Journal ?? '').trim();
        setText('[data-pub="Journal"]', journal);
        $modal.find('#pub-journal-block').toggleClass('d-none', !journal);


        // -----------------------------
        // Block 2: Authors
        // -----------------------------
        const authors = (p.Authors ?? '').trim();
        setText('[data-pub="Authors"]', authors);
        $modal.find('#pub-authors-block').toggleClass('d-none', !authors);

        // -----------------------------
        // Abstract (hide label/section when empty)
        // -----------------------------
        const absRaw = (p.Abstract ?? '').trim();

        const $absPrev = $modal.find('[data-pub="AbstractPreview"]');
        const $absFull = $modal.find('[data-pub="AbstractFull"]');
        const $absBtn = $modal.find('#pub-abstract-toggle');

        $absBtn.off('click');

        const hasAbstract = !!absRaw;
        $modal.find('#pub-abstract-label, #pub-abstract-section')
            .toggleClass('d-none', !hasAbstract);

        if (!hasAbstract) {
            $absPrev.html(EmptyDisplay);
            $absFull.html('').addClass('d-none');
            $absBtn.addClass('d-none').text('Read full abstract');
        } else {
            const plain = absRaw.replace(/<br\s*\/?>/gi, '\n');
            const limit = 650;
            const needsToggle = plain.length > limit;

            const previewPlain = needsToggle ? plain.slice(0, limit).trim() + '…' : plain;

            const toHtml = (txt: string) =>
                txt
                    .replace(/<br\s*\/?>/gi, '\n')
                    .split('\n')
                    .map(s => _.escape(s))
                    .join('<br>');

            $absPrev.html(toHtml(previewPlain)).toggleClass('d-none', false);
            $absFull.html(toHtml(absRaw)).addClass('d-none');

            if (needsToggle) {
                $absBtn.removeClass('d-none').text('Read full abstract');
                $absBtn.on('click', () => {
                    const expanded = !$absFull.hasClass('d-none');
                    $absFull.toggleClass('d-none', expanded);
                    $absPrev.toggleClass('d-none', !expanded);
                    $absBtn.text(expanded ? 'Read full abstract' : 'Show less');
                });
            } else {
                $absBtn.addClass('d-none').text('Read full abstract');
            }
        }

        // -----------------------------
        // Identifiers
        // -----------------------------
        const pmid = (p.PubMedId ?? '').trim();
        const doi = (p.Doi ?? '').trim();
        const pmcid = (p.Pmcid ?? '').trim();

        const $pmidWrap = $modal.find('#pub-id-pmid');
        const $pmcidWrap = $modal.find('#pub-id-pmcid');
        const $doiWrap = $modal.find('#pub-id-doi');

        const $pmidA = $modal.find('[data-pub="PmidLink"]');
        const $pmcidA = $modal.find('[data-pub="PmcidLink"]');
        const $doiA = $modal.find('[data-pub="DoiLink"]');

        $pmidWrap.toggleClass('d-none', !pmid);
        $pmidA.text(pmid);
        if (pmid) $pmidA.attr('href', `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(pmid)}/`);
        else $pmidA.removeAttr('href');

        $pmcidWrap.toggleClass('d-none', !pmcid);
        $pmcidA.text(pmcid);
        if (pmcid) $pmcidA.attr('href', `https://pmc.ncbi.nlm.nih.gov/articles/${encodeURIComponent(pmcid)}/`);
        else $pmcidA.removeAttr('href');

        $doiWrap.toggleClass('d-none', !doi);
        $doiA.text(doi);
        if (doi) $doiA.attr('href', `https://doi.org/${encodeURIComponent(doi)}`);
        else $doiA.removeAttr('href');

        const hasIds = !!(pmid || pmcid || doi);
        $modal.find('#pub-ids-label, #pub-ids-section').toggleClass('d-none', !hasIds);

        // Footer PubMed button
        const $pubmedLink = $modal.find('#pub-pubmed-link');
        if (pmid) $pubmedLink.attr('href', `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(pmid)}/`).removeClass('d-none');
        else $pubmedLink.attr('href', '#').addClass('d-none');
    }

    // Added for Publication refresh table timestamp

    private formatLocalRazorStyle(d: Date): string {
        if (!d || isNaN(d.getTime())) return '';
        return `${FormatDate(d, "day-mo-year")}`;
    }

    // Warning for missing award number and center column tooltip

    private wireSimpleTooltips(): void {
        const tip = document.getElementById('simple-tooltip');
        if (!tip) return;

        const show = (el: HTMLElement) => {
            const msg = el.getAttribute('data-tooltip') || '';
            if (!msg.trim()) return;
            tip.textContent = msg;
            tip.classList.remove('d-none');
        };

        const hide = () => tip.classList.add('d-none');

        const position = (clientX: number, clientY: number) => {
            // offset from cursor
            const pad = 12;
            let x = clientX + pad;
            let y = clientY + pad;

            // keep on-screen
            const rect = tip.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            if (x + rect.width > vw - 8) x = vw - rect.width - 8;
            if (y + rect.height > vh - 8) y = vh - rect.height - 8;

            tip.style.left = `${x}px`;
            tip.style.top = `${y}px`;
        };

        // Delegated events (works with DT redraw + FixedColumns clones)
        document.addEventListener('mouseenter', (e) => {
            const el = (e.target as HTMLElement)?.closest?.('.js-tip') as HTMLElement | null;
            if (!el) return;
            show(el);
        }, true);

        document.addEventListener('mousemove', (e) => {
            if (tip.classList.contains('d-none')) return;
            position((e as MouseEvent).clientX, (e as MouseEvent).clientY);
        }, true);

        document.addEventListener('mouseleave', (e) => {
            const el = (e.target as HTMLElement)?.closest?.('.js-tip');
            if (!el) return;
            hide();
        }, true);

        // safety: hide on scroll/click
        document.addEventListener('scroll', hide, true);
        document.addEventListener('click', hide, true);

    }

  
    //============================
    // CHART of PUBLICATIONS
    //============================

    //Build KPI cards
  
    private kpiRow?: KpiRow;
    // row -> card values
    private getKpiCards(rows: PubDtRow[]): KpiCardVm[] {
        const total = rows.length;

        const last12 = rows.filter(
            r => isInLast12Months(r.PublicationDateUtc)
        ).length;

        const last12Previous = rows.filter(
            r => isInPrevious12Months(r.PublicationDateUtc)
        ).length;

        const preprintsLast12 = rows.filter(
            r => r.IsPreprint && isInLast12Months(r.PublicationDateUtc)
        ).length;

        const preprintsPrevious12 = rows.filter(
            r => r.IsPreprint && isInPrevious12Months(r.PublicationDateUtc)
        ).length;

        const last12Trend = buildCountTrend(last12, last12Previous, {
            label: 'vs prior 12 months'
        });

        const preprintTrend = buildCountTrend(preprintsLast12, preprintsPrevious12, {
            label: 'vs prior 12 months'
        });

        const preprintsSelected = this.state.preprints;
        const preprintActive =
            !!preprintsSelected &&
            preprintsSelected.length === 1 &&
            preprintsSelected[0] === 'Yes';


        const journals = new Set<string>();

        rows
            .filter(r => !r.IsPreprint)
            .forEach(r => {
                const journal = (r.Journal ?? '').trim();
                if (journal) journals.add(journal);
            });

        const avgPreprintToPublished = this.getAveragePreprintToPublishedDisplay(rows);
        //KpiCardVM
        return [
            {
                id: 'total',
                title: 'Total',
                value: total
            },
            {
                id: 'last12',
                title: 'Publications in Last 12 Months',
                value: last12,
                isClickable: true,
                active: this.state.range === 'last12',
                trendText: last12Trend.trendText,
                direction: last12Trend.direction
            },
            {
                id: 'preprint',
                title: 'Preprints in Last 12 Months',
                value: preprintsLast12,
                isClickable: true,
                active: preprintActive,
                trendText: preprintTrend.trendText,
                direction: preprintTrend.direction
            },
            {
                id: 'journals',
                title: 'Total Journals',
                value: journals.size
            },
            {
                id: 'average-time',
                title: 'Average Time from Preprint to Published',
                value: avgPreprintToPublished.value,
                direction: 'flat'
            }
        ];
    }

    private getAveragePreprintToPublishedDisplay(rows: PubDtRow[]): {
        value: string;
        pairCount: number;
    } {
    
        const durations = rows
            .map(r => r.DaysFromPreprintToPublished)
            .filter((d): d is number => d != null && d >= 0);

        if (durations.length === 0) {
            return {
                value: '—',
                pairCount: 0
            };
        }

        const avgDays = Math.round(
            durations.reduce((sum, d) => sum + d, 0) / durations.length
        );

        const months = Math.floor(avgDays / 30);
        const days = avgDays % 30;
        //mo days count smaller font size
        return {
            value: `
        <span class="kpi-main">${months}</span>
        <span class="kpi-sub">months ${days} days</span>
    `,
            pairCount: durations.length
        };
    }

    private getChartData(
        rows: PubDtRow[],
        range: 'all' | 'last12'
    ) {
        const chartRows =
            range === 'all'
                ? rows
                : rows.filter(r => isInLast12Months(r.PublicationDateUtc));

        const centerSet = new Set<string>();

        chartRows.forEach(p => {
            p.CenterNames.forEach(c => centerSet.add(c));
        });

        const categories = Array.from(centerSet).sort((a, b) => a.localeCompare(b));

        const preprint = new Map<string, number>();
        const published = new Map<string, number>();

        const inc = (map: Map<string, number>, key: string) => {
            map.set(key, (map.get(key) ?? 0) + 1);
        };

        chartRows.forEach(p => {
            p.CenterNames.forEach(c => {
                if (p.IsPreprint) inc(preprint, c);
                else inc(published, c);
            });
        });

        return {
            categories,
            series: [
                {
                    key: 'preprint',
                    label: 'Preprint',
                    values: categories.map(c => preprint.get(c) ?? 0),
                    color: '#10426c'
                },
                {
                    key: 'published',
                    label: 'Published',
                    values: categories.map(c => published.get(c) ?? 0),
                    color: '#05af55'
                }
            ]
        };
    }

    //4. Define Click meaning for visuals, click = filter
    // shared helpers in filter-ui-helpers.ts for updating selection in ui
    private registerKpiVisualization(): void {
        this.kpiRow = new KpiRow('#kpi-row-id', (id) => {
            if (id === 'last12') {
                this.state.range = this.state.range === 'last12' ? 'all' : 'last12';
                this.triggerFilter();
                return;
            }
            const isActive =
                this.state.preprints?.length === 1 &&
                this.state.preprints[0] === 'Yes';

            if (isActive) {
                const $preprint = $('#sel-pubs-preprint');

                // None selected = no filter
                $preprint.find('option').prop('selected', false);

                try { ($preprint as any).selectpicker('refresh'); } catch { }
            } else {
                setSingleDropdownOptionByText('#sel-pubs-preprint', 'Yes');
            }

            this.triggerFilter();
        });
    }

    private registerChartCardVisualization(): void {
        this.chartCard = new ChartCard({
            rootSelector: '#chart-card',
            chartSelector: '#pub-status-by-center-chart',
          
            initialRange: this.state.range,
          
            onClearSelection: () => {
                const $center = $('#sel-pubs-center');
                $center.find('option').prop('selected', false);
                try { ($center as any).selectpicker('refresh'); } catch { }

                this.triggerFilter();
            },
           
            onRangeChange: (range) => {
                this.state.range = range;
                this.triggerFilter();
            },
            //filter mapping
            //request= category,seriesKey, rangemode
            //So clicks become filters
            onFilterSelect: (request) => {
                if (request.category) {
                    setSingleDropdownOptionByText('#sel-pubs-center', request.category);
                }

                if (request.range) {
                    this.state.range = request.range;
                }

                this.triggerFilter();
            }
        });
    }


    private renderKpis(rows: PubDtRow[]): void {
        this.kpiRow?.render(this.getKpiCards(rows));
    }
    private renderChartCard(rows: PubDtRow[]): void {
        if (!this.chartCard) return;

        const chartData = this.getChartData(rows, this.state.range);

        this.chartCard.setRange(this.state.range);
        this.chartCard.setChartData(chartData);

        this.chartCard.setActiveSelection(this.getCenterSelectionText());
    }

    private syncPublicationsVisualsToVisibleRows(): void {
        if (!this.dtPubsStandalone) return;

        const visibleRows = this.dtPubsStandalone
            .rows({ search: 'applied' })
            .data()
            .toArray() as PubDtRow[];

        this.updatePubsCount();
        this.renderKpis(visibleRows);
        this.renderChartCard(visibleRows);
        this.renderDonut(visibleRows);
        this.renderRankChart(visibleRows);
       
    }
    
    //donut
    //Donut Chart
    private donutChart?: DonutChart;

    private getDonutData(rows: PubDtRow[]): DonutChartData {
        const preprintCount = rows.filter(r => r.IsPreprint).length;
        const publishedCount = rows.filter(r => !r.IsPreprint).length;

        return {
            title: 'Publication Status',
            total: rows.length,
            slices: [
                {
                    key: 'preprint',
                    label: 'Preprint',
                    value: preprintCount,
                    color: '#10426c'
                },
                {
                    key: 'published',
                    label: 'Published',
                    value: publishedCount,
                    color: '#05af55'
                }
            ]
        };
    }
    private registerDonutVisualization(): void {
        this.donutChart = new DonutChart({
            rootSelector: '#donut-chart-id',

            onSliceClick: (slice) => {
                if (slice.key === 'preprint') {
                    setSingleDropdownOptionByText('#sel-pubs-preprint', 'Yes');
                }

                if (slice.key === 'published') {
                    setSingleDropdownOptionByText('#sel-pubs-preprint', 'No');
                }

                this.triggerFilter();
            },

            onClearSelection: () => {
                const $preprint = $('#sel-pubs-preprint');

                // Clear = no preprint filter = Yes + No deselected
                $preprint.find('option').prop('selected', false);

                try { ($preprint as any).selectpicker('refresh'); } catch { }

                this.triggerFilter();
            }
        });
    }
    private renderDonut(rows: PubDtRow[]): void {
        this.donutChart?.setData(this.getDonutData(rows));
        this.donutChart?.setActiveSelection(this.getPreprintSelectionText());

    }
    //RANK CHART----
    private rankChart?: RankChart;

    private getTopJournalRankData(rows: PubDtRow[]) {
        const counts = new Map<string, number>();

        rows.forEach((row) => {
            if (row.IsPreprint) return;

            const journalName = row.Journal;
            if (!journalName) return;

            counts.set(journalName, (counts.get(journalName) ?? 0) + 1);
        });

        const topFive = Array.from(counts.entries())
            .sort((a, b) => {
                // higher publication count first
                if (b[1] !== a[1]) return b[1] - a[1];
                // stable alphabetical tie-break
                return a[0].localeCompare(b[0]);
            })
            .slice(0, 5);

        const bgColor = '#05af55';
        const textColor = '#fff';


        const fillByRank: Record<1 | 2 | 3 | 4 | 5, number> = {
            1: 100,
            2: 90,
            3: 80,
            4: 70,
            5: 60
        };

        return {
            title: 'Top 5 Journals',
            items: topFive.map(([journalName, publicationCount], index) => {
                const rank = (index + 1) as 1 | 2 | 3 | 4 | 5;

                return {
                    rank,
                    journalName,
                    publicationCount,
                    bgColor,
                    textColor,
                    fillPercent: fillByRank[rank]
                };
            })
        };
    }

    private registerTopJournalRankVisualization(): void {
        this.rankChart = new RankChart({
            rootSelector: '#rank-chart-id',
            onBarClick: (journalName) => {
                setSingleDropdownOptionByText('#sel-pubs-journal', journalName);
                this.triggerFilter();
            },
            onClearSelection: () => {
                $('#sel-pubs-journal').find('option').prop('selected', false);
                try { ($('#sel-pubs-journal') as any).selectpicker('refresh'); } catch { }

                this.triggerFilter();
            }
        });
    }
    private renderRankChart(rows: PubDtRow[]): void {
        this.rankChart?.setData(this.getTopJournalRankData(rows));
        this.rankChart?.setActiveSelection(this.getJournalSelectionText());
    }
    private getJournalSelectionText(): string | null {
        return getSelectionText(this.state.journals, 'Journal', {
            treatEmptyAsAll: true
        });
    }
    private getCenterSelectionText(): string | null {
        return getSelectionText(this.state.centers, 'Center');
    }

    private getPreprintSelectionText(): string | null {
        return getSelectionText(this.state.preprints, 'Preprint', {
            requireSingle: true
        });
    }

    //debounce search rendering
    private pubsSearchSyncTimer?: number;

    private schedulePublicationsVisualSync(): void {
        window.clearTimeout(this.pubsSearchSyncTimer);

        this.pubsSearchSyncTimer = window.setTimeout(() => {
            this.syncPublicationsVisualsToVisibleRows();
        }, 100);
    }

}
